import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { NumberInput } from '../../../components/NumberInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Landmark, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { fmtCLP } from '../egresos/egresosUtils';
import { ContactoSearch } from './ContactoSearch';
import { CuentaContableSearch } from './CuentaContableSearch';
import { FacturasCobroSelector } from './FacturasCobroSelector';

const hoyISO = () => new Date().toISOString().slice(0, 10);
const fechaCL = (iso) => (iso ? iso.split('-').reverse().join('-') : '');

// Registrar Recibo de Caja (payment type=in en Alegra). `contexto` viene de /contexto-movimiento cuando se abre desde un abono.
export function IngresoCrearDialog({ open, onOpenChange, onCreated, contexto }) {
  const mov = contexto?.movimiento || null;
  const [bancos, setBancos] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [fecha, setFecha] = useState(hoyISO());
  const [bancoId, setBancoId] = useState('20');
  const [medio, setMedio] = useState('transfer');
  const [modo, setModo] = useState('facturas');
  const [facturasSel, setFacturasSel] = useState({});
  const [cuenta, setCuenta] = useState(null);
  const [monto, setMonto] = useState('');
  const [glosa, setGlosa] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/contabilidad/egresos/bancos').then((r) => setBancos(r.data.bancos || [])).catch(() => setBancos([]));
    setCliente(contexto?.cliente_sugerido || null);
    setFecha(mov?.fecha || hoyISO());
    setBancoId(String(contexto?.banco_id_sugerido || 20));
    setMedio('transfer'); setModo('facturas'); setFacturasSel({}); setCuenta(null);
    setMonto(mov ? String(mov.abono) : ''); setGlosa('');
  }, [open, contexto, mov]);

  const sumaFacturas = Object.values(facturasSel).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const total = modo === 'facturas' ? sumaFacturas : (parseFloat(monto) || 0);
  const objetivo = mov ? mov.abono : null;
  const calza = !objetivo || Math.abs(total - objetivo) <= 0.5;

  const handleCrear = async () => {
    if (!cliente) { toast.error('Selecciona el cliente / contraparte'); return; }
    if (modo === 'facturas' && Object.keys(facturasSel).length === 0) { toast.error('Selecciona al menos una factura'); return; }
    if (modo === 'cuenta' && !cuenta) { toast.error('Selecciona la cuenta contable (contracuenta)'); return; }
    if (total <= 0) { toast.error('El monto debe ser mayor a 0'); return; }
    if (!calza) { toast.error(`El total (${fmtCLP(total)}) debe coincidir con el abono (${fmtCLP(objetivo)})`); return; }
    setGuardando(true);
    try {
      const body = {
        cliente_alegra_id: String(cliente.alegra_id), fecha_pago: fecha, banco_id: parseInt(bancoId, 10), payment_method: medio, descripcion: glosa,
        facturas: modo === 'facturas' ? Object.entries(facturasSel).map(([id, m]) => ({ invoice_id: id, monto: parseFloat(m) || 0 })) : [],
        categoria_id: modo === 'cuenta' ? String(cuenta.id) : null, monto: modo === 'cuenta' ? total : null,
        movimiento_id: mov?.id || null,
      };
      const r = await api.post('/contabilidad/ingresos', body);
      const d = r.data || {};
      if (d.conciliacion?.error) toast.warning(`Recibo de Caja N°${d.alegra_number} creado en Alegra, pero no se pudo conciliar: ${d.conciliacion.error}`, { duration: 8000 });
      else toast.success(`Recibo de Caja N°${d.alegra_number || d.alegra_payment_id} creado en Alegra${d.conciliacion ? ' y abono conciliado' : ''}`);
      onOpenChange(false); onCreated?.(d);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error registrando el Recibo de Caja'); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="modal-crear-ingreso">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5 text-emerald-600" /> Registrar Recibo de Caja</DialogTitle>
          <DialogDescription>Crea el pago recibido (type=in) en Alegra y su espejo en el ERP. La contracuenta puede ser Cuentas por Cobrar (facturas del cliente) o cualquier cuenta contable (anticipos, fondos por rendir, devoluciones…).</DialogDescription>
        </DialogHeader>
        {mov && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900" data-testid="contexto-abono">
            Abono de la cartola <b>{fechaCL(mov.fecha)}</b> · <b>{fmtCLP(mov.abono)}</b> · {mov.detalle}. El abono quedará <b>conciliado</b> con este Recibo de Caja al crearlo.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Cliente / contraparte (Alegra) *</Label>
            <ContactoSearch value={cliente} onChange={(c) => { setCliente(c); setFacturasSel({}); }} testid="cliente" />
          </div>
          <div>
            <Label className="text-xs">Fecha *</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="text-sm" data-testid="input-fecha-ingreso" />
          </div>
          <div>
            <Label className="text-xs">Cuenta bancaria (Alegra)</Label>
            <Select value={bancoId} onValueChange={setBancoId}>
              <SelectTrigger className="text-sm" data-testid="select-banco-ingreso"><SelectValue placeholder="Banco" /></SelectTrigger>
              <SelectContent>
                {bancos.map((b) => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}
                {bancos.length === 0 && <SelectItem value="20">Banco de Chile 1</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Medio</Label>
            <Select value={medio} onValueChange={setMedio}>
              <SelectTrigger className="text-sm" data-testid="select-medio-ingreso"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transfer">Transferencia</SelectItem>
                <SelectItem value="deposit">Depósito</SelectItem>
                <SelectItem value="check">Cheque</SelectItem>
                <SelectItem value="cash">Efectivo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Contracuenta</Label>
            <Select value={modo} onValueChange={setModo}>
              <SelectTrigger className="text-sm" data-testid="select-contracuenta"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="facturas">Cuentas por Cobrar (facturas del cliente)</SelectItem>
                <SelectItem value="cuenta">Otra cuenta contable (anticipo, fondos por rendir, devolución…)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            {modo === 'facturas' ? (
              <FacturasCobroSelector clienteId={cliente?.alegra_id} sel={facturasSel} onChange={setFacturasSel} objetivo={objetivo} />
            ) : (
              <div className="space-y-2">
                <CuentaContableSearch value={cuenta} onChange={setCuenta} />
                <div>
                  <Label className="text-xs">Monto (CLP) *</Label>
                  <NumberInput value={monto} onChange={setMonto} allowDecimal={false} allowNegative={false} className="text-sm tabular-nums" data-testid="input-monto-ingreso" />
                </div>
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Glosa</Label>
            <Textarea value={glosa} onChange={(e) => setGlosa(e.target.value)} rows={2} className="text-sm" placeholder="Ej: Pago facturas 2497 y 2498" data-testid="input-glosa-ingreso" />
          </div>
        </div>
        <DialogFooter>
          <span className={`mr-auto text-xs ${calza ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="total-ingreso">Total: <b>{fmtCLP(total)}</b>{objetivo && !calza ? ` · abono ${fmtCLP(objetivo)}` : ''}</span>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>Cancelar</Button>
          <Button onClick={handleCrear} disabled={guardando || total <= 0 || !calza} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirmar-crear-ingreso">
            {guardando ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Registrando…</> : 'Registrar en Alegra'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
